/**
 * Sprint 945W — Sentry instrument file (loaded FIRST in index.js).
 *
 * Loaded BEFORE React mounts so it can capture errors during the very
 * first render.  No-op when REACT_APP_SENTRY_DSN is missing/empty.
 *
 * PII mode: strict.  We strip:
 *   - `email` from user context (only `id` survives)
 *   - `Authorization`, `Cookie`, `x-api-key` headers from breadcrumbs
 *   - token-like params from URLs (`token`, `jwt`, `api_key`, etc.)
 */
import * as Sentry from "@sentry/react";

const DSN = process.env.REACT_APP_SENTRY_DSN || "";
const ENV = process.env.REACT_APP_SENTRY_ENVIRONMENT || "production";
const RELEASE = process.env.REACT_APP_SENTRY_RELEASE || undefined;

let TRACES = 0.2;
const traceEnv = process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE;
if (traceEnv && !Number.isNaN(parseFloat(traceEnv))) {
  TRACES = parseFloat(traceEnv);
}

const QS_PII = /(?:^|[?&])(token|jwt|api_key|apiKey|password|verify_token|access_token|refresh_token)=([^&\s#]+)/gi;
const PII_KEYS = new Set([
  "password", "pwd", "passwd",
  "token", "jwt", "access_token", "refresh_token",
  "verify_token", "session_token", "stripe_token",
  "authorization", "cookie", "set-cookie",
  "email",
  "x-api-key", "api-key", "api_key",
  "client_secret",
]);

function scrubObj(o) {
  if (!o || typeof o !== "object") return o;
  const out = Array.isArray(o) ? [] : {};
  for (const [k, v] of Object.entries(o)) {
    if (PII_KEYS.has(String(k).toLowerCase())) {
      out[k] = "[scrubbed]";
    } else if (typeof v === "object") {
      out[k] = scrubObj(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

function beforeSend(event) {
  try {
    if (event.request) {
      if (event.request.headers) event.request.headers = scrubObj(event.request.headers);
      if (event.request.data) event.request.data = scrubObj(event.request.data);
      if (event.request.cookies) event.request.cookies = "[scrubbed]";
      if (typeof event.request.url === "string") {
        event.request.url = event.request.url.replace(QS_PII, (_, k) => `${k}=[scrubbed]`);
      }
      if (typeof event.request.query_string === "string") {
        event.request.query_string = event.request.query_string.replace(
          QS_PII, (_, k) => `${k}=[scrubbed]`
        );
      }
    }
    if (event.breadcrumbs) {
      for (const bc of event.breadcrumbs) {
        if (bc && bc.data) bc.data = scrubObj(bc.data);
      }
    }
    // user — keep id only
    if (event.user) {
      const id = event.user.id;
      event.user = id ? { id } : {};
    }
    if (event.extra) event.extra = scrubObj(event.extra);
  } catch (_) { /* never crash the SDK */ }
  return event;
}

if (DSN) {
  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE,
    tracesSampleRate: TRACES,
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    beforeSend,
    initialScope: {
      tags: { service: "studioplaz-frontend" },
    },
  });
  // Convenience for the Trigger Test Error button.
  window.__sentryEnabled = true;
} else {
  window.__sentryEnabled = false;
  // eslint-disable-next-line no-console
  console.info("[sentry] REACT_APP_SENTRY_DSN not set — frontend errors will NOT be reported.");
}

export const sentryEnabled = Boolean(DSN);
export default Sentry;
